import React from 'react';
import {
  Container, Box, Typography, Grid, Card, CardContent, Button, Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigateAndScroll } from '../utils/navigation';
import { getActiveColleges } from '../data/colleges';
import CollegeLogo from '../components/common/CollegeLogo';
import SEO from '../components/common/SEO';

const HeroSection = styled(Box)({
  background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)',
  color: 'white',
  padding: '64px 0',
  textAlign: 'center',
});

const CollegeCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': { transform: 'translateY(-5px)', boxShadow: theme.shadows[8] },
}));

function CollegesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const colleges = getActiveColleges();

  return (
    <Box>
      <SEO title="Our Colleges" description="CWC recruits cybersecurity students from GCU, ASU, NAU, UOA, UOP, and UAT. Arizona's top cybersecurity programs." />
      <HeroSection>
        <Container maxWidth="lg">
          <Chip label="Our Colleges" sx={{ mb: 3, backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }} />
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Where We Recruit
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.95, maxWidth: 700, mx: 'auto' }}>
            We recruit from Arizona's top cybersecurity programs to deliver expert-level assessments.
          </Typography>
        </Container>
      </HeroSection>

      {/* Why These Colleges */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
          Why These Schools
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 3 }}>
          CWC is not a staffing agency. We are a training platform. We recruit from colleges with strong cybersecurity programs because their students have completed rigorous coursework. We provide the supervised, real-world field experience that turns classroom knowledge into career-ready skills. We are actively working with program directors at each school to formalize our relationship for internship and practicum credit.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
          When you book an assessment, you choose which college to support. Your assessment fee directly funds paid opportunities for cybersecurity students at that institution, strengthening the local talent pipeline and investing in your community's digital future.
        </Typography>
      </Container>

      {/* College Cards */}
      <Box sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {colleges.map((college) => (
              <Grid item xs={12} sm={6} md={3} key={college.id}>
                <CollegeCard>
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <CollegeLogo college={college} height={140} />
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                      {college.name}
                    </Typography>
                    <Chip label={college.location} size="small" sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {college.description}
                    </Typography>
                  </CardContent>
                </CollegeCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* What Students Get */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
          What Students Gain
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {[
            { title: 'Hands-On Field Experience', desc: 'Perform real cybersecurity assessments for real clients in real homes and businesses. No simulations, no lab exercises.' },
            { title: '$140 Per Assessment', desc: 'Top-tier pay for part-time work that fits around class schedules. Plus $10 referral bonuses for every client they bring in.' },
            { title: 'CISSP-Certified Mentorship', desc: 'Every assessment is reviewed by an expert with 10+ years of experience who provides detailed feedback and coaching.' },
            { title: 'Verifiable Professional Portfolio', desc: 'Documented client engagements that employers can verify. CWC experience on a resume stands out in a stack of classroom-only candidates.' },
            { title: 'Career Growth Path', desc: 'Top performers can advance from Intern to Senior Intern to Specialist, with the potential for permanent positions as CWC expands.' },
            { title: 'Industry Network', desc: 'Build relationships with CISSP-certified mentors, college program directors, fellow interns, and clients across Arizona.' },
          ].map((item, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Typography variant="h4" sx={{ color: '#7c3aed', fontWeight: 800, lineHeight: 1 }}>{i + 1}</Typography>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{item.desc}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Partner With Us */}
      <Box sx={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)', py: 8 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" color="white" gutterBottom sx={{ fontWeight: 700 }}>
            Want to Partner With Us?
          </Typography>
          <Typography variant="h6" color="white" sx={{ mb: 2, opacity: 0.95 }}>
            We are expanding across Arizona and nationally. If your college has a cybersecurity program and you want to give your students real-world experience while they earn competitive pay, we want to talk.
          </Typography>
          <Typography variant="body1" color="white" sx={{ mb: 4, opacity: 0.8 }}>
            CWC handles recruitment, vetting, client matching, expert review, and payment. Your college provides the students and the academic foundation.
          </Typography>
          <Button variant="contained" size="large"
            onClick={() => navigateAndScroll(navigate, '/contact', location.pathname)}
            sx={{ backgroundColor: '#fbbf24', color: '#1e1e1e', border: '2px solid #fbbf24', boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
              px: 5, py: 1.5, fontSize: '1.2rem', fontWeight: 700,
              '&:hover': { backgroundColor: '#f59e0b', border: '2px solid #f59e0b', boxShadow: '0 6px 25px rgba(0,0,0,0.5)' } }}>
            Contact Us About Partnering
          </Button>
        </Container>
      </Box>
    </Box>
  );
}

export default CollegesPage;
